import * as Icons from "lucide-react";
import { CTA, GlassCard, PageHeader, SectionHeading, Shell } from "../components/site/Primitives";

const PHASES = [
  ["Automation audit", "We map your enquiry sources, response times, booking process and follow-up gaps.", ["Current workflow map", "Bottleneck list", "Automation shortlist"]],
  ["System design", "We select the modules, define AI scope, escalation rules and the pipeline your team will use.", ["Module plan", "AI instructions", "Pipeline design"]],
  ["Build & configure", "We configure the CRM, workflows, calendar logic, templates and reporting for your business.", ["CRM setup", "Automations", "Templates"]],
  ["Integrate", "You connect the providers you use — voice, calendar, messaging and payments — with your own credentials.", ["Provider connection", "Test calls", "Verification"]],
  ["Launch", "We run a controlled launch with a checklist, then monitor executions and outcomes.", ["Launch checklist", "Monitoring", "Team training"]],
  ["Improve", "Monthly review of conversion, response time and automation performance, then refinement.", ["Performance review", "Workflow tuning", "New agents"]],
];

const ONBOARDING = [
  "Business name", "Industry", "Location / timezone", "Services", "Business hours", "Calendar",
  "Phone / voice", "SMS / WhatsApp", "AI receptionist", "Team", "Automation preferences", "Launch checklist",
];

export default function HowItWorks() {
  return (
    <div data-testid="how-it-works-page">
      <PageHeader
        testId="how-it-works-title"
        eyebrow="How it works"
        title="A defined process, not an open-ended experiment."
        sub="Six phases from audit to continuous improvement, with a documented setup wizard your team completes once."
      />

      <section className="py-20">
        <Shell>
          <div className="grid gap-3 lg:grid-cols-3">
            {PHASES.map(([title, detail, items], i) => (
              <GlassCard key={title} testId={`phase-card-${i}`} className="p-6">
                <span className="font-mono text-[11px] text-elv-green">
                  PHASE {String(i + 1).padStart(2, "0")}
                </span>
                <p className="mt-4 text-base font-semibold text-elv-text">{title}</p>
                <p className="mt-2.5 text-sm leading-relaxed text-elv-dim">{detail}</p>
                <ul className="mt-5 space-y-2 border-t border-elv-line pt-4">
                  {items.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-xs text-elv-dim">
                      <Icons.Check className="mt-0.5 h-3 w-3 shrink-0 text-elv-cyan" />
                      {it}
                    </li>
                  ))}
                </ul>
              </GlassCard>
            ))}
          </div>
        </Shell>
      </section>

      <section className="border-y border-elv-line bg-[#070A0F] py-20">
        <Shell>
          <SectionHeading
            testId="onboarding-heading"
            eyebrow="Client onboarding"
            title="A twelve-step setup wizard inside the platform."
            sub="Progress is tracked, so a partially completed setup is never mistaken for a live system."
          />
          <div className="mt-12 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {ONBOARDING.map((step, i) => (
              <div
                key={step}
                data-testid={`onboarding-step-${i}`}
                className="flex items-center gap-3 rounded-md border border-white/[0.06] bg-white/[0.02] p-4"
              >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-sm border border-elv-green/25 bg-elv-green/[0.07] font-mono text-[11px] text-elv-green">
                  {i + 1}
                </span>
                <p className="text-sm text-elv-text">{step}</p>
              </div>
            ))}
          </div>
          <div className="mt-12">
            <CTA to="/book-demo" testId="how-it-works-cta">
              Automate My Business
            </CTA>
          </div>
        </Shell>
      </section>
    </div>
  );
}
